import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db, storage } from './firebase';
import { signOut } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { validateUserProfile } from './validation';

interface Location {
  latitude: number;
  longitude: number;
}

function Profile(): React.ReactElement {
  const navigate = useNavigate();
  const user = auth.currentUser;
  const [name, setName] = useState<string>('');
  const [dateOfBirth, setDateOfBirth] = useState<string>('');
  const [location, setLocation] = useState<Location | null>(null);
  const [locationError, setLocationError] = useState<string>('');
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [pictureUrl, setPictureUrl] = useState<string>('');
  const [uploadingPicture, setUploadingPicture] = useState<boolean>(false);
  const [pictureError, setPictureError] = useState<string>('');
  const [initialLoadComplete, setInitialLoadComplete] = useState<boolean>(false);

  // Helper function to capitalize name
  const capitalizeName = (name: string): string => {
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Handle picture upload
  const handlePictureUpload = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPictureError('');

    if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
      setPictureError('Only .jpg and .png files are allowed');
      setTimeout(() => setPictureError(''), 3000);
      return;
    }

    if (!user) return;

    setUploadingPicture(true);

    try {
      console.log('Starting image processing...');

      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Failed to load image'));
        image.src = URL.createObjectURL(file);
      });

      console.log('Image loaded, dimensions:', img.width, 'x', img.height);

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }

      canvas.width = 200;
      canvas.height = 200;

      const size = Math.min(img.width, img.height);
      const x = (img.width - size) / 2;
      const y = (img.height - size) / 2;

      ctx.drawImage(img, x, y, size, size, 0, 0, 200, 200);
      URL.revokeObjectURL(img.src);

      console.log('Image drawn to canvas, converting to blob...');

      const mimeType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      const dataUrl = canvas.toDataURL(mimeType, 0.95);

      const response = await fetch(dataUrl);
      const blob = await response.blob();

      console.log('Blob created, size:', blob.size, 'bytes');
      console.log('Uploading to Firebase Storage...');

      const fileExtension = file.type === 'image/png' ? 'png' : 'jpg';
      const storageRef = ref(storage, `profile-pictures/${user.uid}/profile.${fileExtension}`);
      await uploadBytes(storageRef, blob);

      console.log('Upload complete, getting download URL...');

      const downloadUrl = await getDownloadURL(storageRef);

      console.log('Download URL obtained, updating Firestore...');

      setPictureUrl(downloadUrl);

      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        pictureUrl: downloadUrl,
      });

      console.log('Profile picture upload complete!');
      setUploadingPicture(false);
    } catch (error) {
      console.error('Error uploading picture:', error);
      setPictureError('Failed to upload picture: ' + (error instanceof Error ? error.message : 'Unknown error'));
      setTimeout(() => setPictureError(''), 5000);
      setUploadingPicture(false);
    }
  };

  // Get device location
  const getDeviceLocation = (): void => {
    setLocationError('');
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setLocationError('');
      },
      (error) => {
        setLocationError('Unable to retrieve your location: ' + error.message);
      }
    );
  };

  // Load user profile from Firestore
  useEffect(() => {
    const loadProfile = async (): Promise<void> => {
      if (!user) return;

      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const data = userDoc.data();

        validateUserProfile(data);

        const displayName = data.name || user.displayName || '';
        setName(capitalizeName(displayName));
        setDateOfBirth(data.dateOfBirth || '');
        setLocation(data.location || null);
        setPictureUrl(data.pictureUrl || '');
      } else {
        // If no document exists yet, use Google display name as default
        setName(capitalizeName(user.displayName || ''));
      }

      const adminDocRef = doc(db, "admins", user.uid);
      const adminDoc = await getDoc(adminDocRef);
      setIsAdmin(adminDoc.exists());

      setLoading(false);
      setInitialLoadComplete(true);
    };

    loadProfile();
  }, [user]);

  // Auto-save name with debounce
  useEffect(() => {
    if (!initialLoadComplete || !user) return;

    const timeoutId = setTimeout(async () => {
      const capitalizedName = capitalizeName(name);
      if (capitalizedName !== name) {
        setName(capitalizedName);
      }

      try {
        const userDocRef = doc(db, "users", user.uid);
        await updateDoc(userDocRef, {
          name: capitalizedName,
        });
      } catch (error) {
        console.error('Error auto-saving name:', error);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [name, user, initialLoadComplete]);

  // Auto-save dateOfBirth
  useEffect(() => {
    if (!initialLoadComplete || !user) return;

    const saveDate = async () => {
      try {
        const userDocRef = doc(db, "users", user.uid);
        await updateDoc(userDocRef, {
          dateOfBirth: dateOfBirth,
        });
      } catch (error) {
        console.error('Error auto-saving date of birth:', error);
      }
    };

    saveDate();
  }, [dateOfBirth, user, initialLoadComplete]);

  // Auto-save location
  useEffect(() => {
    if (!initialLoadComplete || !user) return;

    const saveLocation = async () => {
      try {
        const userDocRef = doc(db, "users", user.uid);
        await updateDoc(userDocRef, {
          location: location,
        });
      } catch (error) {
        console.error('Error auto-saving location:', error);
      }
    };

    saveLocation();
  }, [location, user, initialLoadComplete]);

  const handleLogout = (): void => {
    signOut(auth);
  };

  if (loading) {
    return <div>Loading profile...</div>;
  }

  return (
    <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
      <h1>My Profile</h1>

      <div style={{ marginBottom: '20px', textAlign: 'center' }}>
        {pictureUrl ? (
          <img
            src={pictureUrl}
            alt="profile"
            style={{ width: '200px', height: '200px', objectFit: 'cover', borderRadius: '10px' }}
          />
        ) : (
          <div
            style={{
              width: '200px',
              height: '200px',
              backgroundColor: '#e0e0e0',
              display: 'inline-block',
              borderRadius: '10px',
            }}
          />
        )}
        <div style={{ marginTop: '10px' }}>
          <label
            htmlFor="picture-upload"
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              backgroundColor: uploadingPicture ? '#ccc' : '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: uploadingPicture ? 'not-allowed' : 'pointer',
              display: 'inline-block',
            }}
          >
            {uploadingPicture ? 'Uploading.!..' : 'Upload Picture'}
          </label>
          <input
            id="picture-upload"
            type="file"
            accept=".jpg,.jpeg,.png"
            onChange={handlePictureUpload}
            disabled={uploadingPicture}
            style={{ display: 'none' }}
          />
          {!pictureUrl && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
              .jpg or .png (will be resized to 200x200)
            </div>
          )}
          {pictureError && (
            <div style={{ fontSize: '14px', color: '#f44336', marginTop: '5px' }}>
              {pictureError}
            </div>
          )}
        </div>
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
          Name:
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{
            width: '100%',
            padding: '8px',
            fontSize: '16px',
            borderRadius: '4px',
            border: '1px solid #ccc'
          }}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
          Date of Birth:
        </label>
        <input
          type="date"
          value={dateOfBirth}
          onChange={(e) => setDateOfBirth(e.target.value)}
          max={new Date().toISOString().split('T')[0]}
          style={{
            width: '100%',
            padding: '8px',
            fontSize: '16px',
            borderRadius: '4px',
            border: '1px solid #ccc'
          }}
        />
      </div>

      <div style={{ marginBottom: '15px' }}>
        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
          Location:
        </label>
        {location && (
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
            Location saved: {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
          </div>
        )}
        <button
          onClick={getDeviceLocation}
          type="button"
          style={{
            padding: '10px 20px',
            fontSize: '16px',
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginBottom: '10px'
          }}
        >
          Update Location
        </button>
        {locationError && (
          <div style={{ fontSize: '14px', color: '#f44336', marginTop: '5px' }}>
            {locationError}
          </div>
        )}
      </div>

      <div style={{ marginBottom: '15px' }}>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '10px' }}>
          <button
            onClick={() => navigate('/questions')}
            style={{
              padding: '10px 20px',
              fontSize: '16px',
              backgroundColor: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Answer Questions
          </button>

          <button
            onClick={() => navigate('/beacons')}
            style={{
              padding: '10px 20px',
              fontSize: '16px',
              backgroundColor: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            My Beacon
          </button>
        </div>

        <button
          onClick={() => navigate('/matches')}
          style={{
            padding: '10px 20px',
            fontSize: '16px',
            backgroundColor: '#E91E63',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            margin: '0 auto 10px',
            display: 'block'
          }}
        >
          View Matches
        </button>

        {isAdmin && (
          <button
            onClick={() => navigate('/admin')}
            style={{
              padding: '10px 20px',
              fontSize: '16px',
              backgroundColor: '#FF9800',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              margin: '0 auto 10px',
              display: 'block'
            }}
          >
            Manage Questions
          </button>
        )}

        <button
          onClick={handleLogout}
          style={{
            padding: '10px 20px',
            fontSize: '16px',
            backgroundColor: '#f44336',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            margin: '0 auto',
            display: 'block'
          }}
        >
          Sign Out
        </button>
      </div>
    </div>
  );
}

export default Profile;
